import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS = {
  matched: ['Matched', 'bg-success/20 text-success'],
  difference: ['Difference', 'bg-error/20 text-error'],
  pending: ['Pending Verification', 'bg-warning/20 text-warning'],
};
const money = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '…');

const VerifyCell = ({ row, onSaved }) => {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  if (row.method === 'cash') return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/cashier/shift/${row.shift_id}/actual-payments`, { method: row.method, amount: val });
      toast.success('Actual total recorded');
      setVal('');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };
  const tid = `${row.shift_id}-${row.method}`;
  return (
    <div className="flex gap-1 justify-end">
      <Input type="number" step="0.01" min="0" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Actual"
        className="h-8 w-24" data-testid={`day-end-verify-input-${tid}`} />
      <Button size="sm" variant="outline" disabled={busy || val === ''} onClick={save} data-testid={`day-end-verify-btn-${tid}`}>Save</Button>
    </div>
  );
};

export const DayEndDifferenceTable = ({ rows = [], totals = {}, canVerify, onChanged }) => {
  const [status, setStatus] = useState('all');
  const [method, setMethod] = useState('all');
  const list = rows.filter((r) => (status === 'all' || r.status === status) && (method === 'all' || r.method === method));
  return (
    <Card className="border-border" data-testid="day-end-difference-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="font-outfit">Day-End Difference</CardTitle>
        <div className="flex gap-2">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-32 h-9" data-testid="day-end-method-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{['all', 'cash', 'card', 'bank', 'online'].map((m) => <SelectItem key={m} value={m}>{m === 'all' ? 'All methods' : m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 h-9" data-testid="day-end-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="day-end-table">
            <thead>
              <tr className="border-b border-border text-left">
                {['Branch', 'Date', 'Shift', 'Cashier', 'Method'].map((h) => <th key={h} className="p-2 font-medium">{h}</th>)}
                {['Expected', 'Actual', 'Difference'].map((h) => <th key={h} className="p-2 font-medium text-right">{h}</th>)}
                <th className="p-2 font-medium text-center">Status</th>
                {canVerify && <th className="p-2 font-medium text-right">Verify</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={`${r.shift_id}-${r.method}`} className="border-b border-border" data-testid={`day-end-row-${r.shift_id}-${r.method}`}>
                  <td className="p-2">{r.branch_name}</td>
                  <td className="p-2">{r.business_date}</td>
                  <td className="p-2 text-xs">{hhmm(r.started_at)} – {hhmm(r.ended_at)}</td>
                  <td className="p-2">{r.cashier_name}</td>
                  <td className="p-2 capitalize">{r.method}</td>
                  <td className="p-2 text-right">{money(r.expected)}</td>
                  <td className="p-2 text-right">{money(r.actual)}</td>
                  <td className={`p-2 text-right font-medium ${r.difference < 0 ? 'text-error' : r.difference > 0 ? 'text-success' : ''}`}>
                    {r.difference > 0 ? '+' : ''}{money(r.difference)}
                  </td>
                  <td className="p-2 text-center"><Badge className={STATUS[r.status][1]} data-testid={`day-end-status-${r.shift_id}-${r.method}`}>{STATUS[r.status][0]}</Badge></td>
                  {canVerify && <td className="p-2">{r.shift_status === 'closed' && <VerifyCell row={r} onSaved={onChanged} />}</td>}
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">No shifts for this day</td></tr>}
            </tbody>
          </table>
        </div>
        <DayEndTotals totals={totals} />
      </CardContent>
    </Card>
  );
};

const DayEndTotals = ({ totals }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="day-end-branch-totals">
    {Object.entries(totals).map(([branch, methods]) => (
      <div key={branch} className="rounded-lg border p-3 text-xs space-y-1" data-testid={`day-end-total-${branch}`}>
        <p className="font-semibold text-sm">{branch}</p>
        {Object.entries(methods).map(([m, t]) => (
          <div key={m} className="flex justify-between gap-2">
            <span className="capitalize">{m}</span>
            <span>Exp {money(t.expected)} · Act {money(t.actual)} · <b className={t.difference < 0 ? 'text-error' : ''}>{money(t.difference)}</b>
              {t.pending > 0 && <span className="text-warning"> · {t.pending} pending</span>}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);
